import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  initGA4,
  trackCallClick,
  trackCtaClick,
  trackEmailClick,
  trackEvent,
  trackOutboundClick,
  trackPageview,
  trackScrollDepth,
} from "@/lib/analytics";
import { captureAttribution } from "@/lib/attribution";
import { logSiteEvent } from "@/lib/site-events";

const CTA_PATTERNS = [
  "request care",
  "schedule a consultation",
  "request a consultation",
  "request this package",
  "book a consultation",
  "talk with us",
  "get started",
  "apply now",
  "start your application",
  "find care",
  "buy",
  "get the kit",
];

/** Internal destinations that mean "this visitor wants a consultation." */
const CONSULTATION_DESTINATIONS = ["/care-request", "/contact"];

const SCROLL_MILESTONES = [25, 50, 75, 90];

/**
 * Mounts GA4 once and fires:
 *  - `page_view` on every client-side route change
 *  - conversion events for phone, email, outbound and primary-CTA clicks
 *    (captured once here via delegation, so no per-link wiring is needed)
 *  - `scroll_depth` engagement milestones (bounce-rate signal)
 *
 * Silently no-ops if VITE_GA4_MEASUREMENT_ID is not set.
 */
const Analytics = () => {
  const { pathname, search } = useLocation();
  const firedDepths = useRef<Set<number>>(new Set());

  // Defer GA4 until the main thread is idle so the tag never competes with LCP.
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      initGA4();
      captureAttribution();
    };
    const idle = (window as typeof window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    const handle = idle ? idle(start, { timeout: 4000 }) : window.setTimeout(start, 2500);
    return () => {
      cancelled = true;
      if (!idle) window.clearTimeout(handle as number);
    };
  }, []);

  useEffect(() => {
    trackPageview(pathname + search);
    logSiteEvent("page_view", pathname);
  }, [pathname, search]);

  // Delegated conversion-click tracking.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest?.("a, button") as HTMLAnchorElement | HTMLButtonElement | null;
      if (!el) return;

      const label = (el.getAttribute("aria-label") || el.textContent || "").trim().toLowerCase();
      const location = window.location.pathname;
      const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") || "" : "";

      if (href.startsWith("tel:")) {
        trackCallClick(location);
        trackEvent("click_to_call", {
          link_location: location,
          link_text: label.slice(0, 100),
        });
        return;
      }
      if (href.startsWith("mailto:")) {
        trackEmailClick(location);
        return;
      }
      if (/^https?:\/\//i.test(href) && !href.includes(window.location.host)) {
        trackOutboundClick(href, location);
        return;
      }
      // Destination-based consultation tracking: any internal navigation to the
      // consultation/contact flow counts, even when the button label varies.
      const isConsultationNav =
        href.startsWith("/") &&
        CONSULTATION_DESTINATIONS.some((dest) => href === dest || href.startsWith(`${dest}?`) || href.startsWith(`${dest}#`));
      const isConsultationLabel = label.includes("consultation") || label.includes("request this package");
      if (isConsultationNav || isConsultationLabel) {
        trackEvent("request_consultation_click", {
          link_location: location,
          link_text: label.slice(0, 100),
          link_destination: href.slice(0, 200) || "(button)",
        });
        trackCtaClick(label || "consultation", location);
        return;
      }
      // Shop interest: any internal navigation into the shop funnel.
      if (href.startsWith("/shop")) {
        logSiteEvent("shop_click", location, href.slice(0, 200));
      }
      if (label && CTA_PATTERNS.some((pattern) => label.includes(pattern))) {
        trackCtaClick(label, location);
      }
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  // Scroll-depth milestones, reset per route.
  useEffect(() => {
    firedDepths.current = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = Math.round((window.scrollY / scrollable) * 100);
      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone && !firedDepths.current.has(milestone)) {
          firedDepths.current.add(milestone);
          trackScrollDepth(milestone, window.location.pathname);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
};

export default Analytics;
